'use client'

import { Checkbox } from '@brew/ui/checkbox'
import { cn } from '@brew/ui/cn'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@brew/ui/select'
import {
  ArrowUpRight,
  Coins,
  type LucideIcon,
  Mail,
  MessagesSquare,
  Palette,
  ShieldCheck,
  UsersRound,
} from 'lucide-react'
import { type ReactNode, useCallback, useId, useState } from 'react'
import {
  MK_CAPTION,
  MK_CARD,
  MK_LABEL,
  MK_SMALL,
} from '@/components/marketing/home/home-layout'
import { ProminentCtaButton } from '@/components/marketing/v2/prominent-cta-button'
import {
  CREDIT_OPTIONS,
  CREDITS_PER_NEW_EMAIL,
  ENTERPRISE_ADD_ONS,
  ENTERPRISE_BASE,
  ENTERPRISE_CTA_LABEL,
  ENTERPRISE_CTA_URL,
  type EnterpriseAddOn,
  emailsDesigned,
  emailsFromCredits,
  formatUsd,
  getEnterpriseQuote,
  type IncludedItem,
  SEND_OPTIONS,
  selfServeUnitRate,
  type VolumeOption,
  volumeLabel,
  volumeSavingsPercent,
} from '@/lib/site/enterprise-content'
import { ICON } from '@/lib/ui/icon'

/** A section's small heading: the marketing caption, tracked uppercase. */
const LABEL = cn(
  MK_CAPTION,
  'font-medium text-mk-fg-2 uppercase tracking-[0.08em]'
)

/** Sections inside the card, ruled apart by the card's own divider. */
const SECTION =
  'flex flex-col gap-4 border-mk-card-divider border-t py-6 first:border-t-0'

/** The green pill on a tier, in the dropdown rows and on the trigger. */
function TierBadge({
  children,
  tone = 'success',
}: {
  children: ReactNode
  /** Green for a saving; neutral for what a tier gets you. */
  tone?: 'success' | 'neutral'
}) {
  return (
    <span
      className={cn(MK_CAPTION, 'rounded-md px-1.5 py-0.5 font-medium', {
        'bg-mk-success/10 text-mk-success': tone === 'success',
        'bg-mk-card text-mk-ink-2 ring-1 ring-mk-line ring-inset':
          tone === 'neutral',
      })}
    >
      {children}
    </span>
  )
}

/**
 * What a tier's pill says: its saving against Pro, or (for credits) how many
 * new emails it designs.
 */
type TierBadgeKind = 'savings' | 'emails'

/** "Save 12%" on a priced tier, or "Best rate" on the one priced on a call. */
function OptionBadge({
  option,
  savings,
  badge,
}: {
  option: VolumeOption | undefined
  savings: number | null
  badge: TierBadgeKind
}) {
  if (option && option.amount === null && option.addUsd === null) {
    return <TierBadge>Best rate</TierBadge>
  }
  if (badge === 'emails') {
    return option?.amount ? (
      <TierBadge tone="neutral">
        {emailsDesigned(option.amount)} emails/mo
      </TierBadge>
    ) : null
  }
  return savings ? <TierBadge>Save {savings}%</TierBadge> : null
}

const INCLUDED_ICONS: Record<IncludedItem['id'], LucideIcon> = {
  support: MessagesSquare,
  privacy: ShieldCheck,
  team: UsersRound,
  brands: Palette,
}

/**
 * Revolut-style: a line icon in a soft round tile at the start of a line.
 * The app's icons, the app's way: Lucide at its default stroke, sized with
 * the product `ICON` scale (`lib/ui/icon.ts`).
 */
function IconCircle({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span
      aria-hidden="true"
      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-mk-tile text-mk-ink"
    >
      <Icon className={ICON.md} />
    </span>
  )
}

/** An icon, a title that leads with the number, and a line under it. */
function IconLine({
  icon,
  title,
  titleId,
  children,
}: {
  icon: LucideIcon
  title: ReactNode
  titleId?: string
  children?: ReactNode
}) {
  return (
    <div className="flex items-start gap-3">
      <IconCircle icon={icon} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-[5px]">
        <span id={titleId} className={cn(MK_SMALL, 'font-medium text-mk-ink')}>
          {title}
        </span>
        {children ? (
          <span className={cn(MK_SMALL, 'text-mk-body')}>{children}</span>
        ) : null}
      </div>
    </div>
  )
}

/** The documents behind a line, for a procurement reviewer to open. */
function DocLinks({ links }: { links: NonNullable<IncludedItem['links']> }) {
  return (
    <span className="mt-3.5 flex flex-wrap gap-x-4 gap-y-1.5">
      {links.map((link) => (
        <a
          key={link.href}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-0.5 text-muted-foreground text-xs transition-colors hover:text-foreground"
        >
          {link.label}
          <ArrowUpRight aria-hidden="true" className={ICON.sm} />
        </a>
      ))}
    </span>
  )
}

function optionPrice(option: VolumeOption): string {
  return option.addUsd === null ? 'Custom' : formatUsd(option.addUsd)
}

/** "250,000 AI credits" for the trigger; the open step reads "More than 1M". */
function optionTitle(
  options: ReadonlyArray<VolumeOption>,
  index: number,
  unit: string
): string {
  const option = options[index]
  if (!option || option.amount === null) {
    return `More than ${volumeLabel(options, options.length - 2)} ${unit}`
  }
  return `${option.amount.toLocaleString('en-US')} ${unit}`
}

/** "25,000 AI credits a month"; the open tier reads "More than 250,000 …". */
function volumeHeadline(
  options: ReadonlyArray<VolumeOption>,
  index: number,
  unit: string
): string {
  const option = options[index]
  const largest = options.at(-2)?.amount ?? 0
  return option?.amount === null || !option
    ? `More than ${largest.toLocaleString('en-US')} ${unit} a month`
    : `${option.amount.toLocaleString('en-US')} ${unit} a month`
}

function PricePerMonth({ option }: { option: VolumeOption }) {
  return (
    <span className="text-mk-fg-2 tabular-nums">
      {optionPrice(option)}
      {option.addUsd === null ? null : (
        <span className="text-[12px]"> /mo</span>
      )}
    </span>
  )
}

/**
 * A dropdown of volume tiers, Lovable-style: each row the amount, a green
 * "Save X%" badge, and its price a month. The closed trigger reads the same.
 */
function VolumeSelect({
  icon,
  title,
  unit,
  options,
  value,
  onChange,
  hint,
  selfServeRate,
  badge = 'savings',
}: {
  icon: LucideIcon
  /** The line's title, leading with the chosen amount. */
  title: ReactNode
  unit: string
  options: ReadonlyArray<VolumeOption>
  value: number
  onChange: (index: number) => void
  /** What the chosen amount gets you, under the title. */
  hint?: ReactNode
  /** Pro's price per unit, the badges' baseline. */
  selfServeRate: number
  /** What the tier pills show; savings by default. */
  badge?: TierBadgeKind
}) {
  const labelId = useId()
  const selected = options[value]
  const selectedSavings = volumeSavingsPercent(options, value, selfServeRate)
  return (
    <div className="flex flex-col gap-4">
      <IconLine icon={icon} title={title} titleId={labelId}>
        {hint}
      </IconLine>
      <Select
        value={String(value)}
        onValueChange={(next) => onChange(Number(next))}
      >
        <SelectTrigger
          aria-labelledby={labelId}
          // From `sm`, indented to the text column (the 32px icon and its
          // 12px gap) so it lines up with the line and hint above it; a phone
          // keeps the full width. 40px tall, the Book a call button's height;
          // the shared trigger sizes itself through `data-[size=default]`.
          className={cn(
            MK_LABEL,
            'w-full cursor-pointer rounded-xl border-mk-line bg-mk-card px-4 text-mk-ink shadow-none hover:border-mk-ring data-[size=default]:h-10 *:data-[slot=select-value]:flex-1 sm:ml-11 sm:w-[calc(100%-2.75rem)]'
          )}
        >
          <SelectValue>
            <span className="flex w-full min-w-0 items-center justify-between gap-3 pr-2">
              <span className="min-w-0 truncate">
                <span className="hidden sm:inline">
                  {optionTitle(options, value, unit)}
                </span>
                <span className="sm:hidden">{volumeLabel(options, value)}</span>
              </span>
              <span
                className={cn(MK_SMALL, 'flex shrink-0 items-center gap-2')}
              >
                <OptionBadge
                  option={selected}
                  savings={selectedSavings}
                  badge={badge}
                />
                {selected ? <PricePerMonth option={selected} /> : null}
              </span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="rounded-xl border-mk-card-line bg-mk-card p-1 shadow-mk-card">
          {options.map((option, index) => {
            const savings = volumeSavingsPercent(options, index, selfServeRate)
            return (
              <SelectItem
                key={volumeLabel(options, index)}
                value={String(index)}
                className={cn(
                  MK_SMALL,
                  'my-0.5 cursor-pointer rounded-lg py-2.5 pl-3 text-mk-ink first:mt-0 last:mb-0 focus:bg-mk-tile/60 data-[state=checked]:bg-mk-tile data-[state=checked]:font-medium *:[span]:last:flex-1'
                )}
              >
                <span className="flex w-full items-center gap-3">
                  <span className="min-w-[80px] font-medium tabular-nums">
                    {option.amount === null
                      ? volumeLabel(options, index)
                      : option.amount.toLocaleString('en-US')}
                  </span>
                  <span className="ml-auto flex items-center gap-2">
                    <OptionBadge
                      option={option}
                      savings={savings}
                      badge={badge}
                    />
                    <span className="min-w-[72px] text-right">
                      <PricePerMonth option={option} />
                    </span>
                  </span>
                </span>
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </div>
  )
}

/**
 * One add-on as a ruled row: the shared checkbox where the other lines have
 * their icon (so every title lines up), the name with its price on the
 * right, and what it is underneath. The whole row toggles it.
 */
function AddOnRow({
  addOn,
  isChecked,
  onChange,
}: {
  addOn: EnterpriseAddOn
  isChecked: boolean
  onChange: (isChecked: boolean) => void
}) {
  const id = useId()
  return (
    <li className="border-mk-card-divider border-t py-1 first:border-t-0 first:pt-0 last:pb-0">
      <label
        htmlFor={id}
        className={cn(
          '-mx-3 flex cursor-pointer items-start gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-mk-tile',
          { 'bg-mk-tile': isChecked }
        )}
      >
        <span className="flex size-8 shrink-0 items-center justify-center">
          <Checkbox
            id={id}
            checked={isChecked}
            onCheckedChange={(next) => onChange(next === true)}
            className="size-5 cursor-pointer rounded-[5px] border-mk-ring bg-mk-card data-[state=checked]:border-mk-accent data-[state=checked]:bg-mk-accent"
          />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 pt-[5px]">
          <span className="flex items-start justify-between gap-3">
            <span className={cn(MK_SMALL, 'font-medium text-mk-ink')}>
              {addOn.name}
            </span>
            <span
              className={cn(
                MK_SMALL,
                'shrink-0 font-medium text-mk-ink tabular-nums'
              )}
            >
              +{formatUsd(addOn.priceUsd)}
            </span>
          </span>
          <span className={cn(MK_SMALL, 'text-mk-body')}>
            {addOn.description}
          </span>
        </span>
      </label>
    </li>
  )
}

/**
 * The page's one menu: build an Enterprise plan line by line. Credits and
 * emails picked from volume tiers, the optional add-ons, and last what the
 * platform fee includes, with a summary beside them (under them on phones)
 * that adds it up, the fee last there too, and books the call.
 */
export function EnterpriseBuilder() {
  const [creditIndex, setCreditIndex] = useState(0)
  const [sendIndex, setSendIndex] = useState(0)
  const [addOns, setAddOns] = useState<ReadonlyArray<EnterpriseAddOn['id']>>([])
  const toggleAddOn = useCallback(
    (id: EnterpriseAddOn['id'], isChecked: boolean) =>
      setAddOns((current) =>
        isChecked ? [...current, id] : current.filter((other) => other !== id)
      ),
    []
  )
  const quote = getEnterpriseQuote({ creditIndex, sendIndex, addOns })
  const rates = selfServeUnitRate()

  return (
    <div className="mx-auto grid w-full max-w-[1000px] grid-cols-[minmax(0,1fr)] items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className={cn(MK_CARD, 'px-6 md:px-8')}>
        <section className={SECTION} aria-labelledby="enterprise-usage-title">
          <h3 id="enterprise-usage-title" className={LABEL}>
            Usage
          </h3>
          <div className="flex flex-col gap-6">
            <VolumeSelect
              icon={Mail}
              title={volumeHeadline(SEND_OPTIONS, sendIndex, 'emails sent')}
              unit="emails"
              options={SEND_OPTIONS}
              value={sendIndex}
              onChange={setSendIndex}
              selfServeRate={rates.email}
              hint="Outside your monthly allowance, extra emails are added to your next invoice."
            />
            <VolumeSelect
              icon={Coins}
              title={volumeHeadline(CREDIT_OPTIONS, creditIndex, 'AI credits')}
              unit="AI credits"
              options={CREDIT_OPTIONS}
              value={creditIndex}
              onChange={setCreditIndex}
              selfServeRate={rates.credit}
              badge="emails"
              hint={
                <>
                  Enough to design about{' '}
                  <span className="font-semibold text-mk-ink">
                    {emailsFromCredits(
                      CREDIT_OPTIONS[creditIndex]?.amount ?? 0
                    )}
                  </span>
                  . A new email uses about {CREDITS_PER_NEW_EMAIL} credits;
                  edits, audits and images usually use less.
                </>
              }
            />
          </div>
        </section>
        <section className={SECTION} aria-labelledby="enterprise-addons-title">
          <h3 id="enterprise-addons-title" className={LABEL}>
            Add-ons
          </h3>
          <ul className="flex flex-col">
            {ENTERPRISE_ADD_ONS.map((addOn) => (
              <AddOnRow
                key={addOn.id}
                addOn={addOn}
                isChecked={addOns.includes(addOn.id)}
                onChange={(isChecked) => toggleAddOn(addOn.id, isChecked)}
              />
            ))}
          </ul>
        </section>
        <section className={SECTION} aria-labelledby="enterprise-base-title">
          <div className="flex items-baseline justify-between gap-3">
            <h3 id="enterprise-base-title" className={LABEL}>
              Platform fee
            </h3>
            <span
              className={cn(LABEL, 'normal-case tabular-nums tracking-normal')}
            >
              {formatUsd(ENTERPRISE_BASE.priceUsd)}/mo
            </span>
          </div>
          <p className={cn(MK_SMALL, '-mt-2 text-mk-body')}>
            Every Enterprise plan comes with:
          </p>
          <ul className="flex flex-col gap-4">
            {ENTERPRISE_BASE.included.map((item) => (
              <li key={item.id}>
                <IconLine icon={INCLUDED_ICONS[item.id]} title={item.title}>
                  {item.description}
                  {item.links ? <DocLinks links={item.links} /> : null}
                </IconLine>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <aside
        aria-label="Your plan"
        className={cn(MK_CARD, 'flex flex-col gap-5 p-6 lg:sticky lg:top-24')}
      >
        <h3 className={LABEL}>Your plan</h3>
        <ul className="flex flex-col gap-2.5">
          {quote.lines.map((line) => (
            <li
              key={line.label}
              className={cn(
                MK_SMALL,
                'flex items-baseline justify-between gap-3'
              )}
            >
              <span className="text-mk-ink">{line.label}</span>
              <span className="shrink-0 text-mk-fg-2 tabular-nums">
                {line.usd === null ? 'Custom' : formatUsd(line.usd)}
              </span>
            </li>
          ))}
        </ul>
        <div
          aria-live="polite"
          className="flex items-baseline justify-between gap-3 border-mk-card-divider border-t pt-4"
        >
          <span className={cn(MK_LABEL, 'text-mk-ink')}>Total</span>
          <span className="font-medium font-mk-display text-[32px] text-mk-ink tabular-nums leading-9 tracking-[-0.03em]">
            {quote.monthlyUsd === null ? 'Custom' : formatUsd(quote.monthlyUsd)}
            {quote.monthlyUsd === null ? null : (
              <span
                className={cn(
                  MK_CAPTION,
                  'font-normal text-mk-fg-2 tracking-normal'
                )}
              >
                {' '}
                /mo
              </span>
            )}
          </span>
        </div>
        <ProminentCtaButton
          href={ENTERPRISE_CTA_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full justify-center"
        >
          {ENTERPRISE_CTA_LABEL}
        </ProminentCtaButton>
      </aside>
    </div>
  )
}
